import { useScrollReveal } from '@/hooks/useScrollAnimations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { HelpCircle } from 'lucide-react';

const faqs = [
  {
    q: 'What branding services do you offer?',
    a: 'We offer DTG (Direct-to-Garment) printing, sublimation printing, 3D sublimation, monogram & embroidery, mug press, and rhinestone design. From school uniforms to corporate merchandise, we handle it all.',
  },
  {
    q: 'How much do your services cost?',
    a: 'Pricing varies based on the type of service, quantity, and complexity of the design. We offer competitive rates with volume discounts for schools and corporates. Contact us for a free, no-obligation quote tailored to your needs.',
  },
  {
    q: 'What is the typical turnaround time?',
    a: 'Standard orders are completed within 5–10 business days. Rush orders can be accommodated within 2–3 business days at an additional fee. Large bulk orders (500+ items) may require 2–3 weeks.',
  },
  {
    q: 'Do you offer bulk or wholesale pricing?',
    a: 'Yes! We offer tiered pricing for bulk orders. The more you order, the lower the per-unit cost. This is especially popular with schools ordering uniforms and corporates ordering branded merchandise.',
  },
  {
    q: 'Can I see a sample before placing a full order?',
    a: 'Absolutely. We provide digital mock-ups for approval before production begins, and physical samples can be arranged for large orders so you can verify quality and fit.',
  },
  {
    q: 'What areas do you serve?',
    a: 'We operate from Lagos and Ilorin and serve clients across Nigeria. We also ship nationwide and can discuss logistics for orders from other West African countries.',
  },
  {
    q: 'What file formats do you accept for designs?',
    a: "We accept high-resolution files in PNG, SVG, AI, PSD, and PDF formats. If you don't have a design ready, our in-house team can create one for you at an affordable rate.",
  },
  {
    q: 'Do you provide branding consultation?',
    a: "Yes, we offer free branding consultations to help you choose the right products, colours, and techniques that align with your brand identity and budget.",
  },
];

const FAQ = () => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="faq" className="py-24 bg-background">
      <div ref={ref} className="container mx-auto px-4 max-w-3xl">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">FAQ</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">Frequently Asked Questions</h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto">Everything you need to know about our branding services.</p>
        </div>

        <Accordion
          type="single"
          collapsible
          className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-display font-semibold text-foreground hover:text-primary py-5 text-base md:text-lg">
                <span className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  {faq.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pl-8 pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className={`text-center mt-12 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <Button asChild className="bg-primary hover:bg-primary-glow text-primary-foreground">
            <a href="#contact">Contact Us</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
